import React, { useEffect, useMemo, useState } from 'react'
import { Building2, Edit, Filter, Mail, Phone, Plus, Search, Trash2, UserCheck, UserX, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'

interface Client {
  _id: string
  name: string
  companyName?: string
  contactName?: string
  email?: string
  phone?: string
  notes?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

const sortClients = (items: Client[]) => (
  [...items].sort((left, right) => left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' }))
)

const ClientManager: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [clientsPage, setClientsPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)

  const loadClients = async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.clients.list({
        sort: { name: 1 }
      }) as { list?: Client[] }
      setClients(sortClients(response.list || []))
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
      toast.error('Erro ao carregar clientes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadClients()
  }, [])

  const handleCreateClient = async (clientData: Partial<Client>) => {
    try {
      const now = new Date().toISOString()
      const createdClient = await lumi.entities.clients.create({
        ...clientData,
        name: clientData.name?.trim(),
        companyName: clientData.companyName?.trim() || undefined,
        contactName: clientData.contactName?.trim() || undefined,
        email: clientData.email?.trim() || undefined,
        phone: clientData.phone?.trim() || undefined,
        notes: clientData.notes?.trim() || undefined,
        isActive: true,
        createdAt: now,
        updatedAt: now
      }) as Client

      setClients((previous) => sortClients([createdClient, ...previous]))
      setShowCreateModal(false)
      toast.success('Cliente cadastrado com sucesso!')
    } catch (error) {
      console.error('Erro ao cadastrar cliente:', error)
      toast.error('Erro ao cadastrar cliente')
    }
  }

  const handleUpdateClient = async (clientId: string, updates: Partial<Client>) => {
    try {
      const updatedClient = await lumi.entities.clients.update(clientId, {
        ...updates,
        name: updates.name?.trim(),
        companyName: updates.companyName?.trim() || undefined,
        contactName: updates.contactName?.trim() || undefined,
        email: updates.email?.trim() || undefined,
        phone: updates.phone?.trim() || undefined,
        notes: updates.notes?.trim() || undefined,
        updatedAt: new Date().toISOString()
      }) as Client

      setClients((previous) => sortClients(previous.map((client) => (
        client._id === clientId ? { ...client, ...updatedClient } : client
      ))))
      setEditingClient(null)
      toast.success('Cliente atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error)
      toast.error('Erro ao atualizar cliente')
    }
  }

  const handleToggleStatus = async (client: Client) => {
    await handleUpdateClient(client._id, { isActive: !client.isActive })
  }

  const handleDeleteClient = async (client: Client) => {
    if (!window.confirm(`Excluir o cliente "${client.name}"?`)) return

    try {
      await lumi.entities.clients.delete(client._id)
      setClients((previous) => previous.filter((item) => item._id !== client._id))
      toast.success('Cliente excluído com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir cliente:', error)
      toast.error('Erro ao excluir cliente')
    }
  }

  const filteredClients = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return clients.filter((client) => {
      const matchesSearch =
        !query ||
        client.name.toLowerCase().includes(query) ||
        (client.companyName || '').toLowerCase().includes(query) ||
        (client.contactName || '').toLowerCase().includes(query) ||
        (client.email || '').toLowerCase().includes(query)
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && client.isActive) ||
        (statusFilter === 'inactive' && !client.isActive)

      return matchesSearch && matchesStatus
    })
  }, [clients, searchTerm, statusFilter])

  const clientsTotalPages = Math.max(1, Math.ceil(filteredClients.length / 10))
  const clientsCurrentPage = Math.min(clientsPage, clientsTotalPages)
  const clientsPageItems = filteredClients.slice((clientsCurrentPage - 1) * 10, clientsCurrentPage * 10)

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
        >
          <Plus size={20} />
          <span>Novo Cliente</span>
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {filteredClients.length === 0 ? (
          <div className="py-12 text-center">
            <Building2 size={44} className="mx-auto mb-3 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">
              {searchTerm || statusFilter !== 'all' ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
            </h3>
            <p className="mt-1 text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? 'Tente ajustar os filtros de busca.'
                : 'Cadastre clientes para vincular pesquisas e portais de resultado.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {clientsPageItems.map((client) => (
              <div key={client._id} className="grid grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_130px_160px] lg:items-center">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="break-words text-lg font-semibold text-gray-900">{client.name}</h3>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${client.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {client.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  {client.companyName && <p className="mt-1 text-sm text-gray-600">{client.companyName}</p>}
                  <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
                    {client.contactName && <span>{client.contactName}</span>}
                    {client.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail size={14} />
                        {client.email}
                      </span>
                    )}
                    {client.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone size={14} />
                        {client.phone}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleToggleStatus(client)}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                    client.isActive
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  {client.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                  {client.isActive ? 'Desativar' : 'Ativar'}
                </button>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingClient(client)}
                    className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                    title="Editar cliente"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClient(client)}
                    className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                    title="Excluir cliente"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {filteredClients.length > 0 && (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600 sm:flex-row">
            <span>
              Pagina {clientsCurrentPage} de {clientsTotalPages} · {filteredClients.length} registro(s)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={clientsCurrentPage <= 1}
                onClick={() => setClientsPage(clientsCurrentPage - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={clientsCurrentPage >= clientsTotalPages}
                onClick={() => setClientsPage(clientsCurrentPage + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>

      {(showCreateModal || editingClient) && (
        <ClientFormModal
          client={editingClient}
          onSave={editingClient
            ? (data) => handleUpdateClient(editingClient._id, data)
            : handleCreateClient}
          onClose={() => {
            setShowCreateModal(false)
            setEditingClient(null)
          }}
        />
      )}
    </div>
  )
}

const ClientFormModal: React.FC<{
  client?: Client | null
  onSave: (data: Partial<Client>) => void
  onClose: () => void
}> = ({ client, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: client?.name || '',
    companyName: client?.companyName || '',
    contactName: client?.contactName || '',
    email: client?.email || '',
    phone: client?.phone || '',
    notes: client?.notes || '',
    isActive: client?.isActive ?? true
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!formData.name.trim()) {
      toast.error('Informe o nome do cliente.')
      return
    }
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {client ? 'Editar Cliente' : 'Novo Cliente'}
          </h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">Nome do cliente *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(event) => setFormData((previous) => ({ ...previous, name: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Razão social / empresa</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(event) => setFormData((previous) => ({ ...previous, companyName: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Contato principal</label>
              <input
                type="text"
                value={formData.contactName}
                onChange={(event) => setFormData((previous) => ({ ...previous, contactName: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(event) => setFormData((previous) => ({ ...previous, email: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Telefone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(event) => setFormData((previous) => ({ ...previous, phone: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">Observações internas</label>
              <textarea
                value={formData.notes}
                onChange={(event) => setFormData((previous) => ({ ...previous, notes: event.target.value }))}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(event) => setFormData((previous) => ({ ...previous, isActive: event.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Cliente ativo
            </label>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              {client ? 'Salvar Cliente' : 'Cadastrar Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ClientManager